import { useState } from "react";

function getMSE(sphere, cylinder) {
  return (parseFloat(sphere) || 0) + (parseFloat(cylinder) || 0) / 2;
}
function mseToSnellen(mse) {
  const a = Math.abs(mse);
  if (a <= 0.25) return { label: "6/6",  score: 100, desc: "Perfect / 20/20" };
  if (a <= 0.50) return { label: "6/7.5",score: 92,  desc: "Near perfect" };
  if (a <= 0.75) return { label: "6/9",  score: 83,  desc: "Mild blur" };
  if (a <= 1.00) return { label: "6/12", score: 75,  desc: "Mild-moderate" };
  if (a <= 1.50) return { label: "6/18", score: 60,  desc: "Moderate" };
  if (a <= 2.00) return { label: "6/24", score: 45,  desc: "Moderate-severe" };
  if (a <= 3.00) return { label: "6/36", score: 30,  desc: "Severe" };
  if (a <= 4.00) return { label: "6/60", score: 15,  desc: "Very severe" };
  return { label: "<6/60", score: 5, desc: "Severely impaired" };
}
function getAgeContext(age) {
  if (!age) return "";
  const a = parseInt(age);
  if (a < 8)  return "Young child (under 8): Ortho-K myopia control is critical at this age.";
  if (a < 18) return "Child/Teen (8-17): Prime age for Ortho-K; high likelihood of improvement.";
  if (a < 25) return "Young adult (18-24): Good candidate; cornea still adapting well.";
  if (a < 40) return "Adult (25-39): Stable prescription expected; Ortho-K effective for correction.";
  if (a < 50) return "Pre-presbyopic (40-49): Monitor for early presbyopia signs.";
  return "Presbyopic (50+): Expect presbyopia; may need reading glasses alongside Ortho-K.";
}
function getTrend(prev, curr) {
  const d = Math.abs(parseFloat(prev) || 0) - Math.abs(parseFloat(curr) || 0);
  if (d > 0.12) return "improving";
  if (d < -0.12) return "regressing";
  return "stable";
}

function LineChart({ t, data, label, color }) {
  if (!data || data.length < 2) return null;
  const W = 300, H = 110, P = { top: 16, right: 20, bottom: 26, left: 38 };
  const iW = W - P.left - P.right, iH = H - P.top - P.bottom;
  const vals = data.map(d => d.value);
  const minV = Math.min(...vals, 0) - 0.25;
  const maxV = Math.max(...vals) + 0.25;
  const range = maxV - minV || 1;
  const xS = i => P.left + (i / (data.length - 1)) * iW;
  const yS = v => P.top + ((maxV - v) / range) * iH;
  const pts = data.map((d, i) => `${xS(i)},${yS(d.value)}`).join(" ");
  const tY = yS(0);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, marginBottom: 4 }}>{label}</div>
      <svg width={W} height={H} style={{ overflow: "visible", maxWidth: "100%" }}>
        {[0, 0.25, 0.5, 0.75, 1].map(r => {
          const y = P.top + r * iH, v = maxV - r * range;
          return <g key={r}>
            <line x1={P.left} y1={y} x2={W - P.right} y2={y} stroke={t.soft} strokeWidth={1} />
            <text x={P.left - 3} y={y + 4} textAnchor="end" fontSize={8} fill={t.muted}>{v.toFixed(2)}</text>
          </g>;
        })}
        <line x1={P.left} y1={tY} x2={W - P.right} y2={tY} stroke="#4CAF50" strokeWidth={1.5} strokeDasharray="4,3" />
        <text x={W - P.right + 3} y={tY + 4} fontSize={8} fill="#4CAF50">0.00</text>
        <polygon points={`${P.left},${P.top + iH} ${pts} ${W - P.right},${P.top + iH}`} fill={color} opacity={0.08} />
        <polyline points={pts} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => <g key={i}>
          <circle cx={xS(i)} cy={yS(d.value)} r={5} fill={color} stroke="#fff" strokeWidth={2} />
          <text x={xS(i)} y={yS(d.value) - 8} textAnchor="middle" fontSize={9} fill={color} fontWeight="600">{d.value}</text>
        </g>)}
        {data.map((d, i) => <text key={i} x={xS(i)} y={H - 3} textAnchor="middle" fontSize={8} fill={t.muted}>{d.label}</text>)}
      </svg>
    </div>
  );
}

function VisionRing({ t, score, label, snellen, desc, color }) {
  const size = 86, stroke = 7, r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r, dash = circ * Math.min(score / 100, 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={t.soft} strokeWidth={stroke} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color, lineHeight: 1 }}>{snellen}</div>
          <div style={{ fontSize: 9, color: t.muted, marginTop: 1 }}>{score}%</div>
        </div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, color: t.text }}>{label}</div>
      <div style={{ fontSize: 9, color: t.muted, textAlign: "center" }}>{desc}</div>
    </div>
  );
}

export function ProfileModal({ t, profile, onSave, onClose }) {
  const [d, setD] = useState(profile || { name: "", age: "", gender: "", lensType: "Ortho-K", wearingSince: "", targetVision: "20/20 (6/6)", notes: "" });
  const u = (k, v) => setD(p => ({ ...p, [k]: v }));
  const F = ({ label, k, type = "text", placeholder = "" }) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{label}</div>
      <input type={type} value={d[k] || ""} onChange={e => u(k, e.target.value)} placeholder={placeholder}
        style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: 10, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }} />
    </div>
  );
  const S = ({ label, k, opts }) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{label}</div>
      <select value={d[k] || ""} onChange={e => u(k, e.target.value)}
        style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: 10, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" }}>
        {opts.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.card, borderRadius: "24px 24px 0 0", padding: "20px 20px 44px", width: "100%", maxWidth: 430, maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: t.text }}>My profile</div>
          <button onClick={onClose} style={{ background: t.soft, border: "none", borderRadius: 10, width: 32, height: 32, cursor: "pointer", fontSize: 16, color: t.muted }}>✕</button>
        </div>
        <div style={{ textAlign: "center", marginBottom: 18 }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: t.grad, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto" }}>👁️</div>
        </div>
        <F label="Full name" k="name" placeholder="e.g. Alan Ahmad" />
        <F label="Age" k="age" type="number" placeholder="e.g. 28" />
        <S label="Gender" k="gender" opts={["Prefer not to say", "Male", "Female", "Other"]} />
        <S label="Lens type" k="lensType" opts={["Ortho-K", "RGP Hard", "Scleral", "Soft Monthly", "Soft Daily", "Spectacles", "None"]} />
        <F label="Wearing lenses since" k="wearingSince" type="date" />
        <S label="Target vision" k="targetVision" opts={["20/20 (6/6) — Perfect", "20/25 (6/7.5) — Near perfect", "20/30 (6/9) — Functional", "20/40 (6/12) — Driving standard"]} />
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>Notes / concerns</div>
          <textarea value={d.notes || ""} onChange={e => u("notes", e.target.value)} rows={3} placeholder="e.g. Dry eyes, family history of glaucoma..."
            style={{ width: "100%", padding: "10px 12px", border: "1px solid #ddd", borderRadius: 10, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }} />
        </div>
        <button onClick={() => { onSave(d); onClose(); }}
          style={{ width: "100%", padding: 14, borderRadius: 14, border: "none", background: t.grad, color: "#fff", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "inherit" }}>
          Save profile
        </button>
      </div>
    </div>
  );
}

export default function ProgressScreen({ t, rxList, profile }) {
  const [analysing, setAnalysing] = useState(false);
  const [aiInsight, setAiInsight] = useState(null);
  const [aiError, setAiError]     = useState("");
  const [activeEye, setActiveEye] = useState("right");

  if (!rxList || rxList.length === 0) {
    return (
      <div>
        <div style={{ fontWeight: 700, fontSize: 18, color: t.text, marginBottom: 4 }}>Vision progress</div>
        <div style={{ background: t.card, borderRadius: 18, padding: 32, textAlign: "center", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📈</div>
          <div style={{ fontWeight: 700, color: t.text, marginBottom: 6 }}>No prescriptions yet</div>
          <div style={{ fontSize: 13, color: t.muted }}>Upload at least one prescription to start tracking your vision progress toward 20/20.</div>
        </div>
      </div>
    );
  }

  const sorted  = [...rxList].filter(r => r.examDate).sort((a, b) => a.examDate.localeCompare(b.examDate));
  const latest  = sorted[sorted.length - 1];
  const prev    = sorted.length > 1 ? sorted[sorted.length - 2] : null;

  const buildChart = (eye, field) => sorted.map(rx => ({
    value: parseFloat(rx[eye]?.[field]) || 0,
    label: rx.examDate?.slice(2, 7).replace("-", "/") || "?",
  }));

  const rMSE = getMSE(latest.right?.sphere, latest.right?.cylinder);
  const lMSE = getMSE(latest.left?.sphere,  latest.left?.cylinder);
  const rVis = mseToSnellen(rMSE);
  const lVis = mseToSnellen(lMSE);
  const rTrend = prev ? getTrend(prev.right?.sphere, latest.right?.sphere) : "unknown";
  const lTrend = prev ? getTrend(prev.left?.sphere,  latest.left?.sphere)  : "unknown";

  const tIcon  = { improving: "📈", stable: "➡️", regressing: "📉", unknown: "❓" };
  const tColor = { improving: "#4CAF50", stable: "#FF9800", regressing: "#E53935", unknown: t.muted };

  const concerns = [];
  const age = parseInt(profile?.age);
  if (age && age < 12 && Math.abs(rMSE) > 3) concerns.push("⚠️ High myopia in a child — discuss myopia control urgency with your optometrist.");
  if (age && age > 40) concerns.push("ℹ️ Over 40: watch for presbyopia signs — difficulty reading up close.");
  if (Math.abs(rMSE) > 6 || Math.abs(lMSE) > 6) concerns.push("⚠️ High myopia (>6D) — increased risk of retinal issues. Annual dilated exam recommended.");
  if (rTrend === "regressing" || lTrend === "regressing") concerns.push("💡 Regression detected — ensure consistent nightly wear and consult your optometrist.");

  const runAI = async () => {
    if (!prev) return;
    setAnalysing(true); setAiError(""); setAiInsight(null);
    try {
      const res = await fetch("https://eyerx-scan.alanahmad81.workers.dev", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "analyse-progress",
          previousRx: prev, currentRx: latest,
          userAge: profile?.age || "",
          ageContext: getAgeContext(profile?.age),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Analysis failed");
      if (data.parsed) setAiInsight(data.parsed);
      else throw new Error("Could not parse AI response");
    } catch (e) { setAiError(e.message); }
    setAnalysing(false);
  };

  const eyeColor = activeEye === "right" ? t.primary : "#E8829A";
  const card = (children, style = {}) => (
    <div style={{ background: t.card, borderRadius: 18, padding: 16, marginBottom: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", ...style }}>{children}</div>
  );

  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 18, color: t.text, marginBottom: 4 }}>Vision progress</div>
      <div style={{ fontSize: 13, color: t.muted, marginBottom: 16 }}>
        {sorted.length} prescription{sorted.length > 1 ? "s" : ""} tracked
        {profile?.age ? ` · Age ${profile.age}` : ""}
        {profile?.lensType ? ` · ${profile.lensType}` : ""}
      </div>

      {card(<>
        <div style={{ fontWeight: 700, fontSize: 14, color: t.text, marginBottom: 14 }}>Current vision estimate</div>
        <div style={{ display: "flex", justifyContent: "space-around" }}>
          <VisionRing t={t} score={rVis.score} label="Right (OD)" snellen={rVis.label} desc={rVis.desc} color={t.primary} />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
            <div style={{ fontSize: 10, color: t.muted }}>Target</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#4CAF50" }}>6/6</div>
            <div style={{ fontSize: 10, color: "#4CAF50" }}>20/20</div>
          </div>
          <VisionRing t={t} score={lVis.score} label="Left (OS)" snellen={lVis.label} desc={lVis.desc} color="#E8829A" />
        </div>
        <div style={{ fontSize: 11, color: t.muted, textAlign: "center", marginTop: 12, background: t.soft, borderRadius: 8, padding: "6px 10px" }}>
          Estimates based on prescription. Actual vision may differ.
        </div>
      </>)}

      {prev && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          {[{ eye: "Right (OD)", trend: rTrend, curr: latest.right?.sphere, p: prev.right?.sphere },
            { eye: "Left (OS)",  trend: lTrend, curr: latest.left?.sphere,  p: prev.left?.sphere }].map((e, i) => {
            const diff = (parseFloat(e.curr) || 0) - (parseFloat(e.p) || 0);
            return (
              <div key={i} style={{ background: t.card, borderRadius: 14, padding: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <div style={{ fontSize: 11, color: t.muted, marginBottom: 4 }}>{e.eye}</div>
                <div style={{ fontSize: 20 }}>{tIcon[e.trend]}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: tColor[e.trend] }}>{e.trend === "unknown" ? "Not enough data" : e.trend.charAt(0).toUpperCase() + e.trend.slice(1)}</div>
                {diff !== 0 && <div style={{ fontSize: 10, color: t.muted, marginTop: 2 }}>SPH {diff > 0 ? `+${diff.toFixed(2)}` : diff.toFixed(2)}D</div>}
              </div>
            );
          })}
        </div>
      )}

      {sorted.length >= 2 && card(<>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: t.text }}>Prescription trend</div>
          <div style={{ display: "flex", gap: 6 }}>
            {["right", "left"].map(eye => (
              <button key={eye} onClick={() => setActiveEye(eye)}
                style={{ padding: "4px 11px", borderRadius: 18, border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 11, fontWeight: 600,
                  background: activeEye === eye ? (eye === "right" ? t.primary : "#E8829A") : t.soft,
                  color: activeEye === eye ? "#fff" : t.muted }}>
                {eye === "right" ? "OD" : "OS"}
              </button>
            ))}
          </div>
        </div>
        <LineChart t={t} data={buildChart(activeEye, "sphere")} label={`Sphere (SPH) — ${activeEye === "right" ? "Right" : "Left"} eye`} color={eyeColor} />
        {buildChart(activeEye, "cylinder").some(d => d.value !== 0) && (
          <LineChart t={t} data={buildChart(activeEye, "cylinder")} label="Cylinder (CYL) — Astigmatism" color={eyeColor} />
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
          <div style={{ width: 22, height: 2, borderTop: "2px dashed #4CAF50" }} />
          <div style={{ fontSize: 10, color: t.muted }}>Green dashed = target 0.00 (emmetropia / 20/20)</div>
        </div>
      </>)}

      {concerns.length > 0 && (
        <div style={{ background: "#FFF8E1", border: "1px solid #FFE082", borderRadius: 14, padding: "12px 14px", marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: "#F57F17", fontSize: 13, marginBottom: 8 }}>⚠️ Points to watch</div>
          {concerns.map((c, i) => <div key={i} style={{ fontSize: 12, color: "#5D4037", marginBottom: 5, lineHeight: 1.6 }}>{c}</div>)}
        </div>
      )}

      {card(<>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: prev ? 10 : 0 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: t.text }}>🤖 AI analysis</div>
            {prev && <div style={{ fontSize: 11, color: t.muted }}>{prev.examDate} → {latest.examDate}</div>}
          </div>
          {prev && (
            <button onClick={runAI} disabled={analysing}
              style={{ padding: "8px 14px", borderRadius: 12, border: "none", background: analysing ? "#ddd" : t.grad, color: analysing ? "#999" : "#fff", fontWeight: 700, fontSize: 12, cursor: analysing ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              {analysing ? "Analysing…" : aiInsight ? "Re-analyse" : "Analyse"}
            </button>
          )}
        </div>
        {!prev && <div style={{ fontSize: 13, color: t.muted, background: t.soft, borderRadius: 10, padding: "10px 12px" }}>Upload a second prescription to unlock AI comparison analysis.</div>}
        {analysing && (
          <div style={{ padding: "14px 0" }}>
            <div style={{ fontSize: 12, color: t.muted, marginBottom: 8 }}>Comparing prescriptions{profile?.age ? ` for age ${profile.age}` : ""}…</div>
            <div style={{ height: 4, borderRadius: 2, background: t.soft, overflow: "hidden" }}>
              <div style={{ height: "100%", background: t.grad, borderRadius: 2, width: "60%", animation: "scanbar 1.4s ease-in-out infinite" }} />
            </div>
            <style>{`@keyframes scanbar{0%{transform:translateX(-100%)}100%{transform:translateX(260%)}}`}</style>
          </div>
        )}
        {aiError && <div style={{ fontSize: 12, color: "#c62828", background: "#ffebee", borderRadius: 9, padding: "8px 12px", marginTop: 8 }}>⚠️ {aiError}</div>}
        {aiInsight && !analysing && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, background: t.soft, borderRadius: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 26 }}>{tIcon[aiInsight.overallTrend] || "🔍"}</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14, color: tColor[aiInsight.overallTrend] || t.text }}>{aiInsight.overallTrend?.charAt(0).toUpperCase() + aiInsight.overallTrend?.slice(1)}</div>
                <div style={{ fontSize: 13, color: t.text, fontWeight: 600 }}>{aiInsight.headline}</div>
              </div>
            </div>
            <div style={{ fontSize: 13, color: t.text, lineHeight: 1.7, marginBottom: 12 }}>{aiInsight.analysis}</div>
            <div style={{ background: aiInsight.overallTrend === "improving" ? "#E8F5E9" : aiInsight.overallTrend === "regressing" ? "#FFF8E1" : t.soft, borderRadius: 12, padding: "12px 14px", marginBottom: 12, borderLeft: `4px solid ${tColor[aiInsight.overallTrend] || t.primary}` }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: t.text, marginBottom: 4 }}>
                {aiInsight.overallTrend === "improving" ? "🌟 Keep it up!" : aiInsight.overallTrend === "regressing" ? "💪 Don't give up!" : "💬 Progress note:"}
              </div>
              <div style={{ fontSize: 13, color: t.text, lineHeight: 1.7 }}>{aiInsight.motivation}</div>
            </div>
            {aiInsight.tips?.length > 0 && <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: t.text, marginBottom: 8 }}>💡 Tips for better results</div>
              {aiInsight.tips.map((tip, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6, fontSize: 12, color: t.text, lineHeight: 1.6 }}>
                  <span style={{ color: t.primary, fontWeight: 700, flexShrink: 0 }}>{i + 1}.</span><span>{tip}</span>
                </div>
              ))}
            </div>}
          </div>
        )}
      </>)}

      {card(<>
        <div style={{ fontWeight: 700, fontSize: 14, color: t.text, marginBottom: 12 }}>📋 All visits</div>
        {[...sorted].reverse().map((rx, i) => {
          const rv = mseToSnellen(getMSE(rx.right?.sphere, rx.right?.cylinder));
          const lv = mseToSnellen(getMSE(rx.left?.sphere,  rx.left?.cylinder));
          return (
            <div key={rx.id || i} style={{ display: "flex", gap: 12, alignItems: "center", padding: "9px 0", borderBottom: i < sorted.length - 1 ? `1px solid ${t.soft}` : "none" }}>
              <div style={{ width: 40, height: 40, borderRadius: 11, background: i === 0 ? t.grad : t.soft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                {i === 0 ? "🆕" : "📋"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: t.text }}>{rx.examDate}</div>
                  {i === 0 && <span style={{ background: "#E8F5E9", color: "#2E7D32", fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 10 }}>Latest</span>}
                </div>
                <div style={{ fontSize: 11, color: t.muted }}>OD {rv.label} ({rx.right?.sphere || "?"}) · OS {lv.label} ({rx.left?.sphere || "?"})</div>
                {rx.clinic && <div style={{ fontSize: 10, color: t.muted }}>{rx.clinic}</div>}
              </div>
            </div>
          );
        })}
      </>)}
    </div>
  );
}
